import { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { BsInfoCircle } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

import { useCreateToneOfVoice, useUpdateToneOfVoice } from '../../../models/toneOfVoices/mutations';

/**
 * ToneFormModal - Modal for creating/editing custom tones of voice
 * Provides full control over AI prompt instructions
 */
const ToneFormModal = ({ isOpen, onClose, editTone = null }) => {
	const createMutation = useCreateToneOfVoice();
	const updateMutation = useUpdateToneOfVoice();

	const [formData, setFormData] = useState({
		name: '',
		shortDescription: '',
		minWords: 80,
		maxWords: 120,
		aiPromptInstructions: '',
		recommendedFor: '',
	});
	const [errors, setErrors] = useState({});

	const isEditing = Boolean(editTone);
	const isSaving = createMutation.isPending || updateMutation.isPending;

	useEffect(() => {
		if (isOpen) {
			if (editTone) {
				setFormData({
					name: editTone.name || '',
					shortDescription: editTone.shortDescription || '',
					minWords: editTone.targetWordCount?.min || 80,
					maxWords: editTone.targetWordCount?.max || 120,
					aiPromptInstructions: editTone.aiPromptInstructions || '',
					recommendedFor: (editTone.recommendedFor || []).join(', '),
				});
			} else {
				setFormData({
					name: '',
					shortDescription: '',
					minWords: 80,
					maxWords: 120,
					aiPromptInstructions: '',
					recommendedFor: '',
				});
			}
			setErrors({});
		}
	}, [isOpen, editTone]);

	const handleChange = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: null }));
		}
	};

	const validate = () => {
		const newErrors = {};

		if (!formData.name.trim()) {
			newErrors.name = 'Name ist erforderlich';
		}

		if (!formData.shortDescription.trim()) {
			newErrors.shortDescription = 'Kurzbeschreibung ist erforderlich';
		}

		if (!formData.aiPromptInstructions.trim()) {
			newErrors.aiPromptInstructions = 'AI-Anweisungen sind erforderlich';
		} else if (formData.aiPromptInstructions.trim().length < 50) {
			newErrors.aiPromptInstructions = 'AI-Anweisungen sollten mindestens 50 Zeichen lang sein';
		}

		if (formData.minWords >= formData.maxWords) {
			newErrors.maxWords = 'Max Wörter muss größer sein als Min Wörter';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validate()) return;

		const toneData = {
			name: formData.name.trim(),
			shortDescription: formData.shortDescription.trim(),
			aiPromptInstructions: formData.aiPromptInstructions.trim(),
			targetWordCount: {
				min: parseInt(formData.minWords, 10),
				max: parseInt(formData.maxWords, 10),
			},
			recommendedFor: formData.recommendedFor
				.split(',')
				.map((s) => s.trim())
				.filter(Boolean),
		};

		try {
			if (isEditing) {
				await updateMutation.mutateAsync({
					id: editTone.id,
					updates: toneData,
				});
			} else {
				await createMutation.mutateAsync(toneData);
			}
			onClose();
		} catch (error) {
			setErrors({ submit: error.message || 'Fehler beim Speichern' });
		}
	};

	const examplePrompt = `TONE OF VOICE: [Name]

VORGABEN:
- Länge: X-Y Wörter
- Ton: [z.B. professionell, freundlich, direkt]
- Stil: [z.B. formal, locker, sachlich]

STRUKTUR:
1. Einstieg: [Beschreibung]
2. Hauptteil: [Beschreibung]
3. Abschluss: [Beschreibung]

WICHTIG:
- [Regel 1]
- [Regel 2]`;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="m">
			<Panel.Header onClose={onClose}>
				{isEditing ? 'Tone of Voice bearbeiten' : 'Neuen Tone of Voice erstellen'}
			</Panel.Header>
			<Panel.Body>
				<div className="space-y-4">
					<div>
						<label htmlFor="toneName" className="block text-sm font-medium text-text mb-1">
							Name <span className="text-warning-500">*</span>
						</label>
						<TextInput
							id="toneName"
							value={formData.name}
							onChange={(e) => handleChange('name', e.target.value)}
							error={Boolean(errors.name)}
							placeholder="z.B. Mein Tech-Stil"
						/>
						{errors.name && <p className="text-sm text-warning-500 mt-1">{errors.name}</p>}
					</div>

					<div>
						<label htmlFor="toneDescription" className="block text-sm font-medium text-text mb-1">
							Kurzbeschreibung <span className="text-warning-500">*</span>
						</label>
						<TextInput
							id="toneDescription"
							value={formData.shortDescription}
							onChange={(e) => handleChange('shortDescription', e.target.value)}
							error={Boolean(errors.shortDescription)}
							placeholder="Kurze Beschreibung für die Auswahl-Ansicht"
						/>
						{errors.shortDescription && (
							<p className="text-sm text-warning-500 mt-1">{errors.shortDescription}</p>
						)}
					</div>

					<div className="grid grid-cols-2 gap-4">
						<div>
							<label htmlFor="minWords" className="block text-sm font-medium text-text mb-1">
								Min Wörter
							</label>
							<TextInput
								id="minWords"
								type="number"
								min="30"
								max="300"
								value={formData.minWords}
								onChange={(e) => handleChange('minWords', e.target.value)}
							/>
						</div>
						<div>
							<label htmlFor="maxWords" className="block text-sm font-medium text-text mb-1">
								Max Wörter
							</label>
							<TextInput
								id="maxWords"
								type="number"
								min="50"
								max="500"
								value={formData.maxWords}
								onChange={(e) => handleChange('maxWords', e.target.value)}
								error={Boolean(errors.maxWords)}
							/>
							{errors.maxWords && <p className="text-sm text-warning-500 mt-1">{errors.maxWords}</p>}
						</div>
					</div>

					<div>
						<label htmlFor="recommendedFor" className="block text-sm font-medium text-text mb-1">
							Empfohlen für (kommagetrennt)
						</label>
						<TextInput
							id="recommendedFor"
							value={formData.recommendedFor}
							onChange={(e) => handleChange('recommendedFor', e.target.value)}
							placeholder="z.B. IT, Startups, SaaS"
						/>
						<p className="text-xs text-bgBase-800 mt-1">
							Branchen oder Zielgruppen, für die dieser Stil geeignet ist
						</p>
					</div>

					<div>
						<label htmlFor="aiPromptInstructions" className="block text-sm font-medium text-text mb-1">
							AI-Anweisungen (Prompt) <span className="text-warning-500">*</span>
						</label>
						<Textarea
							id="aiPromptInstructions"
							value={formData.aiPromptInstructions}
							onChange={(e) => handleChange('aiPromptInstructions', e.target.value)}
							rows={10}
							error={Boolean(errors.aiPromptInstructions)}
							className="font-mono"
							placeholder={examplePrompt}
						/>
						{errors.aiPromptInstructions && (
							<p className="text-sm text-warning-500 mt-1">{errors.aiPromptInstructions}</p>
						)}
						<div className="flex items-start gap-2 mt-2 p-3 bg-primary-100 rounded-card">
							<BsInfoCircle className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
							<p className="text-xs text-primary-700">
								Diese Anweisungen werden der KI direkt übergeben. Definieren Sie Länge, Ton, Struktur
								und Regeln. Orientieren Sie sich an den System-Stilen als Vorlage.
							</p>
						</div>
					</div>

					{errors.submit && <Alert tone="warning">{errors.submit}</Alert>}
				</div>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
					Abbrechen
				</Button>
				<Button type="button" onClick={handleSubmit} disabled={isSaving}>
					{isSaving ? 'Speichere...' : isEditing ? 'Speichern' : 'Erstellen'}
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

ToneFormModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	editTone: PropTypes.shape({
		id: PropTypes.string.isRequired,
		name: PropTypes.string,
		shortDescription: PropTypes.string,
		aiPromptInstructions: PropTypes.string,
		targetWordCount: PropTypes.shape({
			min: PropTypes.number,
			max: PropTypes.number,
		}),
		recommendedFor: PropTypes.arrayOf(PropTypes.string),
	}),
};

export default ToneFormModal;
